import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import Payments from '../../components/Payments/Payments';
import { OrderContext } from '../../context/OrderContext/order.context';
import { useNavigate } from 'react-router-dom';
import ClientStepper from '../../components/ClientStepper/ClientStepper';
import ClientOrderSummary from '../../components/ClientOrderSummary/ClientOrderSummary';
import { GeoService } from '../../services/geo.services';
import { OrdersService, Prices } from '../../services/orders.services';
import { QuantityPickerContext } from '../../context/QuantityPickerContext/quantity-picker.context';
import ClientStoreBreadcrumbs from '../../components/ClientStoreBreadcrumbs/ClientStoreBreadcrumbs';
import { CUSTOMER_STORE_FLOW, OrderCreationFlow } from '../../types/order-flow';

const ProductsPayment = () => {
	const navigate = useNavigate();
	const { order, additional } = useContext(OrderContext);
	const [quantity, setQuantity] = useState<number>(0);
	const [prices, setPrices] = useState<Prices>({
		productPriceForClient: 0,
		productPriceForClientWithVat: 0,
		transportPriceForClient: 0,
		transportPriceForClientWithVat: 0,
	});
	const [loadingPrices, setLoadingPrices] = useState<boolean>(false);

	const flow: OrderCreationFlow =
		additional?.orderFlow ?? CUSTOMER_STORE_FLOW.BASE;
	const stepNo = flow === CUSTOMER_STORE_FLOW.CONTRACT_LOCATION_FIRST ? 4 : 3;

	const { totalQuantityCounter, inputQuantity } = useContext(
		QuantityPickerContext,
	);

	const currentProduct = useMemo(() => order?.product, [order?.product]);
	const currentCategory = useMemo(() => order?.category, [order?.category]);
	const currentGroup = useMemo(() => order?.group, [order?.group]);

	const isContractOrder = Boolean(order?.contractData);

	const selectedContractLine = useMemo(() => {
		if (
			!isContractOrder ||
			!order?.contractData?.lines?.length ||
			!currentProduct?.id
		) {
			return undefined;
		}
		const pid = Number(currentProduct.id);
		return (
			order.contractData.lines.find(
				(l: any) => (l?.product?.id ?? l?.productId) === pid,
			) ?? order.contractData.lines[0]
		);
	}, [isContractOrder, order?.contractData?.lines, currentProduct?.id]);

	const derivedContractPrices = useMemo(() => {
		if (!selectedContractLine) return undefined;

		const base = Number(selectedContractLine.pricePerTonBuyer ?? 0);
		const markupPct = Number(selectedContractLine.markup ?? 0);
		const vatPct = Number(
			selectedContractLine?.product?.vat ?? currentProduct?.vat ?? 0,
		);

		const pricePerUnit = base;
		const pricePerUnitWithProcent = +(base * (1 + markupPct / 100)).toFixed(2);
		const pricePerUnitWithVat = +(
			pricePerUnitWithProcent *
			(1 + vatPct / 100)
		).toFixed(2);

		return {
			pricePerUnit,
			pricePerUnitWithProcent,
			pricePerUnitWithVat,
		};
	}, [selectedContractLine, currentProduct?.vat]);

	const effectiveProduct = useMemo(() => {
		if (selectedContractLine?.product && derivedContractPrices) {
			return {
				...selectedContractLine.product,
				pricePerUnit: derivedContractPrices.pricePerUnit,
				pricePerUnitWithProcent: derivedContractPrices.pricePerUnitWithProcent,
				pricePerUnitWithVat: derivedContractPrices.pricePerUnitWithVat,
			};
		}
		return currentProduct;
	}, [selectedContractLine?.product, derivedContractPrices, currentProduct]);

	useEffect(() => {
		if (totalQuantityCounter?.length) {
			const result = totalQuantityCounter?.reduce(
				(total, item) => total + item.payload * item.quantity,
				0,
			);
			setQuantity(result);
		} else {
			setQuantity(0);
		}
	}, [totalQuantityCounter]);

	const getPrices = useCallback(async () => {
		try {
			setLoadingPrices(true);
			let quantity: any = 0;
			let trucksNr: any = 1;
			if (totalQuantityCounter?.length) {
				quantity = totalQuantityCounter?.reduce(
					(total: any, item) => total + item.payload * item.quantity,
					0,
				);
				trucksNr = totalQuantityCounter?.reduce(
					(total: any, item) => total + item.quantity,
					0,
				);
			}
			if (inputQuantity) quantity = inputQuantity;

			let latDelivery = 0;
			let longDelivery = 0;

			if (order?.location?.locationId) {
				const resp = await GeoService.getLocationDetails({
					locationid: order?.location?.locationId,
				});
				latDelivery = resp?.data?.Latitude;
				longDelivery = resp?.data?.Longitude;
			} else if (isContractOrder && order?.location) {
				latDelivery = order.location.latitude!;
				longDelivery = order.location.longitude!;
			}

			const { data } = await OrdersService.calculatePrice({
				quantity,
				trucksNr,
				trucks:
					!inputQuantity && totalQuantityCounter?.length
						? totalQuantityCounter.map(({ payload, quantity }) => ({
								payload: Number(payload),
								quantity: Number(quantity),
						  }))
						: undefined,
				latPickup: effectiveProduct?.location?.coordinates?.length
					? effectiveProduct.location.coordinates[1]
					: 47.04014,
				longPickup: effectiveProduct?.location?.coordinates?.length
					? effectiveProduct.location.coordinates[0]
					: 28.80058,
				latDelivery,
				longDelivery,
				productId: effectiveProduct?.id!,
				...(isContractOrder
					? { contractLineId: selectedContractLine?.id }
					: {}),
			});
			setPrices(data);
		} catch (e) {
		} finally {
			setLoadingPrices(false);
		}
	}, [
		inputQuantity,
		order?.location?.locationId,
		totalQuantityCounter,
		effectiveProduct?.id,
		effectiveProduct?.location?.coordinates,
		isContractOrder,
		selectedContractLine?.id,
	]);

	useEffect(() => {
		getPrices().then(() => {});
	}, [getPrices]);

	const onSuccess = (id: number) => {
		navigate(`/store/${id}/thank-you`);
	};

	return (
		<div className="w-full flex flex-col gap-4 h-[calc(100vh-60px)] gap-x-2.5 overflow-x-hidden xl:overflow-x-auto mt-[68px] sm:mt-0">
			<div className="w-[calc(100vw-26px)] sm:w-[1102px] xl:self-center flex flex-col gap-4 pt-3.5 sm:pt-0 mb-1 xl-2xl:mb-4 px-3.5 sm:px-12 xl:ml-0">
				<ClientStoreBreadcrumbs
					currentCategory={currentCategory}
					currentGroup={currentGroup}
					currentProduct={effectiveProduct}
					categoryId={order?.category?.id}
					contract={isContractOrder ? order?.contractData : undefined}
				/>
			</div>
			<div className="border border-solid border-gray-10 border-t-0 border-l-0 border-r-0" />

			<div className="w-[calc(100vw-26px)] sm:w-[1102px] xl:self-center px-3.5 sm:px-12 xl:ml-0">
				<ClientStepper currentStep={stepNo} flow={flow} />
			</div>
			<div className="border border-solid border-gray-10 border-t-0 border-l-0 border-r-0" />

			<div className="sm:w-[calc(100vw-154px)] xl:w-[1133px] xl:self-center flex flex-col sm:flex-row gap-12 mb-6 sm:mb-0 mt-2 xl-2xl:mt-3.5 sm:ml-12 sm:mr-7.5 xl:mx-0">
				<Payments
					onSuccess={onSuccess}
					currentProduct={effectiveProduct!}
					prices={prices}
					quantity={quantity}
					loading={loadingPrices}
				/>

				<div className="hidden sm:inline-table sm:w-[412px] xl:w-[460px]">
					<ClientOrderSummary
						currentProduct={effectiveProduct!}
						prices={prices}
						quantity={quantity}
						step={stepNo}
						loading={loadingPrices}
					/>
				</div>
			</div>
		</div>
	);
};

export default ProductsPayment;
